/**
 * 前端 API 客户端
 * 用途：对接 PHP 后端（商品/案例 CRUD、登录、上传、导入导出）
 * 用法：
 *   import { fetchProducts, loginAdmin } from '@/services/api'
 *   const list = await fetchProducts()
 */

const API_BASE = '/api'
const TOKEN_KEY = 'duogu_admin_token'

export function getToken() {
  return localStorage.getItem(TOKEN_KEY) || ''
}

export function setToken(token) {
  if (token) localStorage.setItem(TOKEN_KEY, token)
  else localStorage.removeItem(TOKEN_KEY)
}

/**
 * 通用请求
 * @param {string} path 如 /products
 * @param {RequestInit & { auth?: boolean }} options
 */
async function request(path, options = {}) {
  const headers = {
    ...(options.headers || {})
  }

  if (!(options.body instanceof FormData)) {
    headers['Content-Type'] = headers['Content-Type'] || 'application/json'
  }

  if (options.auth) {
    const token = getToken()
    if (token) headers.Authorization = `Bearer ${token}`
  }

  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers
  })

  // 导出下载可能是纯 JSON 附件
  const contentType = res.headers.get('content-type') || ''
  if (contentType.includes('application/json')) {
    const json = await res.json()
    if (!res.ok || json.success === false) {
      const err = new Error(json.message || `请求失败 (${res.status})`)
      err.status = res.status
      err.payload = json
      throw err
    }
    return json.data
  }

  if (!res.ok) {
    throw new Error(`请求失败 (${res.status})`)
  }
  return res
}

/** 把相对上传路径补全为可访问地址（开发环境走代理） */
export function resolveMediaUrl(url = '') {
  if (!url) return ''
  if (/^https?:\/\//i.test(url) || url.startsWith('data:')) return url
  if (url.startsWith('/')) return url
  return `/${url.replace(/^\.?\//, '')}`
}

/** 规范化 gallery 内媒体 URL */
export function resolveGallery(gallery = []) {
  return (gallery || []).map((item) => {
    if (typeof item === 'string') return resolveMediaUrl(item)
    return {
      ...item,
      src: resolveMediaUrl(item.src),
      poster: resolveMediaUrl(item.poster || '')
    }
  })
}

// —— 公开接口 ——
/** 提交联系询盘（后端落盘并邮件通知 MAIL_TO） */
export function submitInquiry(payload) {
  return request('/inquiries', {
    method: 'POST',
    body: JSON.stringify(payload)
  })
}

export function fetchProducts() {
  return request('/products')
}

export function fetchProduct(id) {
  return request(`/products/${encodeURIComponent(id)}`)
}

export function fetchCases() {
  return request('/cases')
}

export function fetchCase(id) {
  return request(`/cases/${encodeURIComponent(id)}`)
}

/** 获取类别列表（公开） */
export function fetchCategories() {
  return request('/categories')
}

// —— 管理接口（需登录） ——
export async function loginAdmin(password) {
  const data = await request('/auth/login', {
    method: 'POST',
    body: JSON.stringify({ password })
  })
  setToken(data.token)
  return data
}

export async function logoutAdmin() {
  try {
    await request('/auth/logout', { method: 'POST', auth: true })
  } finally {
    setToken('')
  }
}

export function createProduct(payload) {
  return request('/products', { method: 'POST', auth: true, body: JSON.stringify(payload) })
}

export function updateProduct(id, payload) {
  return request(`/products/${encodeURIComponent(id)}`, {
    method: 'PUT',
    auth: true,
    body: JSON.stringify(payload)
  })
}

export function deleteProduct(id) {
  return request(`/products/${encodeURIComponent(id)}`, { method: 'DELETE', auth: true })
}

export function createCase(payload) {
  return request('/cases', { method: 'POST', auth: true, body: JSON.stringify(payload) })
}

export function updateCase(id, payload) {
  return request(`/cases/${encodeURIComponent(id)}`, {
    method: 'PUT',
    auth: true,
    body: JSON.stringify(payload)
  })
}

export function deleteCase(id) {
  return request(`/cases/${encodeURIComponent(id)}`, { method: 'DELETE', auth: true })
}

export function createCategory(payload) {
  return request('/categories', { method: 'POST', auth: true, body: JSON.stringify(payload) })
}

export function updateCategory(id, payload) {
  return request(`/categories/${encodeURIComponent(id)}`, {
    method: 'PUT',
    auth: true,
    body: JSON.stringify(payload)
  })
}

export function deleteCategory(id) {
  return request(`/categories/${encodeURIComponent(id)}`, { method: 'DELETE', auth: true })
}

export async function uploadFile(file) {
  const form = new FormData()
  form.append('file', file)
  return request('/upload', { method: 'POST', auth: true, body: form })
}

export async function exportResource(resource) {
  const token = getToken()
  const res = await fetch(`${API_BASE}/${resource}/export`, {
    headers: { Authorization: `Bearer ${token}` }
  })
  if (!res.ok) throw new Error('导出失败')
  const blob = await res.blob()
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `${resource}-${Date.now()}.json`
  a.click()
  URL.revokeObjectURL(url)
}

export function importResource(resource, items, mode = 'replace') {
  return request(`/${resource}/import`, {
    method: 'POST',
    auth: true,
    body: JSON.stringify({ items, mode })
  })
}
